<template>
  <div class="voyage-map-component">
    <div id="voyage-map" ref="mapContainer" class="map-container"></div>

    <div v-if="loading" class="map-loading-overlay">
      <b-spinner label="Loading map..." variant="primary"></b-spinner>
      <p class="mt-2 text-secondary">Loading voyage track...</p>
    </div>
  </div>
</template>

<script>
import { useVoyageMap } from '@/views/voyage/composables/useVoyageMap';

export default {
  name: 'VoyageMap',
  props: {
    voyage: {
      type: Object,
      default: null,
    },
    gpsData: {
      type: Object,
      default: null,
    },
    asset: {
      type: Object,
      default: null,
    },
    originLandmark: {
      type: Object,
      default: null,
    },
    destinationLandmark: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const {
      loadLeafletCSS,
      ensureLeafletLoaded,
      initMap,
      drawGpsTrack,
      addShipToMap,
      addDotTrailMarkers,
      addLandmarkMarkers,
      destroyMap,
    } = useVoyageMap();

    return {
      loadLeafletCSS,
      ensureLeafletLoaded,
      initMap,
      drawGpsTrack,
      addShipToMap,
      addDotTrailMarkers,
      addLandmarkMarkers,
      destroyMap,
    };
  },
  data() {
    return {
      mapInitialized: false,
      coordinatesData: [],
    };
  },
  watch: {
    gpsData: {
      handler(newData) {
        if (newData && this.mapInitialized) {
          this.onGpsDataLoaded();
        }
      },
    },
    asset: {
      handler(newAsset) {
        if (newAsset && this.mapInitialized && this.gpsData) {
          this.onAssetLoaded();
        }
      },
    },
    originLandmark: {
      handler(newOrigin) {
        if (newOrigin && this.mapInitialized) {
          this.onLandmarksLoaded();
        }
      },
    },
    destinationLandmark: {
      handler(newDestination) {
        if (newDestination && this.mapInitialized) {
          this.onLandmarksLoaded();
        }
      },
    },
  },
  async mounted() {
    this.loadLeafletCSS();
    await this.ensureLeafletLoaded();
    this.initVoyageMap();
  },
  beforeDestroy() {
    this.destroyMap();
  },
  methods: {
    async initVoyageMap() {
      const map = this.initMap('voyage-map');
      if (map) {
        this.mapInitialized = true;
        this.$emit('map-initialized', map);

        if (this.gpsData) {
          this.onGpsDataLoaded();
        }
      }
    },
    onGpsDataLoaded() {
      const { coordinatesData } = this.drawGpsTrack(this.gpsData);
      this.coordinatesData = coordinatesData;
      this.$emit('track-drawn', { coordinatesData });

      if (this.asset) {
        this.onAssetLoaded();
      }
    },
    onAssetLoaded() {
      const success = this.addShipToMap(this.asset, this.gpsData, this.voyage);
      if (success) {
        this.$emit('ship-added');
        // Add dot trail markers after ship icons are created
        const markersAdded = this.addDotTrailMarkers(this.coordinatesData);
        this.$emit('dot-trail-added', { markersAdded });
      }
    },
    onLandmarksLoaded() {
      if (this.originLandmark || this.destinationLandmark) {
        this.addLandmarkMarkers(this.originLandmark, this.destinationLandmark);
      }
    },
  },
};
</script>

<style scoped>
.voyage-map-component {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 400px;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  background: #fff;
  overflow: hidden;
}

.map-container {
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.map-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  z-index: 1000;
  border-radius: 0.375rem;
}

/* Ship icon styles */
:deep(.ship-icon) {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

:deep(.heading-icon) {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

:deep(.marker-icon) {
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}

:deep(.ship-label) {
  font-size: 0.8125rem;
  font-weight: 700;
  text-align: center;
  color: #fff;
  padding: 0.1875rem 0.3125rem;
  border-radius: 0.25rem;
  white-space: nowrap;
}

:deep(.ship-label.ship-online) {
  background-color: #198754;
}

:deep(.ship-label.ship-offline) {
  background-color: #dc3545;
}

:deep(.ship-label.ship-no-gps) {
  background-color: #9CA3AF;
}

:deep(.ship-label.ship-no-sensor) {
  background-color: #F97316;
}

/* Landmark markers (Origin & Destination) */
:deep(.landmark-marker) {
  background: transparent;
  border: none;
}

:deep(.landmark-marker .marker-badge) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-weight: 700;
  font-size: 16px;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

:deep(.origin-marker .marker-badge) {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
}

:deep(.destination-marker .marker-badge) {
  background: linear-gradient(135deg, #dc3545 0%, #ff6b6b 100%);
}

/* Leaflet popup styles */
:deep(.leaflet-popup-content-wrapper) {
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
  background-color: #fff !important;
  color: #212529;
  font-weight: 500;
  width: 280px;
  padding: 0.3125rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

:deep(.leaflet-popup-content) {
  margin: 0.625rem;
  font-size: 0.75rem;
  line-height: 1.5;
}

/* Map settings control */
:deep(.voyage-map-settings) {
  background: #fff;
  border-radius: 0.375rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

:deep(.voyage-map-settings .settings-toggle) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: #fff;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
}

:deep(.voyage-map-settings .settings-toggle:hover) {
  background: #f8f9fa;
  color: #0d6efd;
}

:deep(.voyage-map-settings .settings-toggle i) {
  font-size: 18px;
}

:deep(.voyage-map-settings .settings-panel) {
  min-width: 140px;
  padding: 0.5rem 0;
}

:deep(.voyage-map-settings .settings-panel.hidden) {
  display: none;
}

:deep(.voyage-map-settings .settings-header) {
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #6c757d;
  border-bottom: 1px solid #e9ecef;
}

:deep(.voyage-map-settings .settings-separator) {
  height: 1px;
  background: #e9ecef;
  margin: 0.5rem 0;
}

:deep(.voyage-map-settings .settings-option) {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: background 0.2s ease;
}

:deep(.voyage-map-settings .settings-option:hover) {
  background: #f8f9fa;
}

:deep(.voyage-map-settings .settings-option input[type="radio"]),
:deep(.voyage-map-settings .settings-option input[type="checkbox"]) {
  margin: 0 0.5rem 0 0;
  accent-color: #0d6efd;
}

:deep(.voyage-map-settings .settings-option span) {
  font-size: 0.875rem;
  color: #212529;
}
</style>
